/**
 * Embed mode: the app running inside something that owns the chrome around
 * it. Two kinds:
 *
 * - **frame** (#11021): a panel inside another product's page, as a
 *   cross-origin iframe or with `?embed=iframe`. The rest of this comment is
 *   about this mode.
 * - **app** (#9163): the Finpilot Notetaker app's web view, which appends
 *   `FinpilotNotetaker/<version> (<platform>; build <build>)` to its user
 *   agent. The app signs in natively, owns the navigation, and is the user's
 *   own. What differs from frame mode is listed in
 *   `.claude/features/9163-ios-notetaker-app.md`, *fpui: app mode*.
 *
 * Frame mode: the app running as a panel inside another product's page.
 *
 * The consequences are presentational: the application navigation is removed,
 * and the frame stays on record and interaction pages (`EmbedFrame`). The
 * frame was read-only until #15113; it is now read-write, and the viewer's
 * permission alone decides what they may change, as in the full app.
 *
 * **This is not a security boundary and must not be described as one.** The
 * embedded viewer holds an ordinary session with their ordinary rights and could
 * open the app in another tab.
 *
 * ## Why the trigger is structural rather than a URL parameter
 *
 * This originally read `?embed=iframe` from the URL. That was not robust: the
 * app rewrites its own query string constantly (`FpPages/Layout` calls
 * `history.replaceState` on most viewer interactions; the RMS index does
 * `navigate("/rms", { replace: true })`), and under code-splitting this module
 * can evaluate *after* the first navigation — so the parameter could already be
 * gone by the time it was read. The result was navigation reappearing inside the
 * frame, intermittently, because it was a load-order race.
 *
 * Being framed by another origin cannot be dropped by a navigation, does not
 * depend on module load order, needs no storage, and is constant for the
 * lifetime of the document. Evaluating it late is therefore harmless.
 */

const EMBED_PARAM = "embed";
const EMBED_VALUE = "iframe";

/**
 * Whether a *different origin* is framing this document.
 *
 * Deliberately not the simpler `window.self !== window.top`. Storybook and
 * vitest browser mode render stories inside a **same-origin** iframe, so that
 * check would put the entire test suite into embed mode and silently break
 * hundreds of unrelated stories. A same-origin frame is our own harness; only a
 * cross-origin frame is a third-party embed.
 *
 * The probe is reading `window.top.location` — permitted for a same-origin
 * parent, and a SecurityError for a cross-origin one.
 */
function detectCrossOriginFrame(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.self === window.top) return false;
    // Throws iff the parent is another origin.
    void window.top?.location.href;
    return false;
  } catch {
    return true;
  }
}

/**
 * The URL this document was *loaded* with.
 *
 * `PerformanceNavigationTiming.name` is the navigated URL and is unaffected by
 * later `pushState` / `replaceState`, so the override below survives the app
 * rewriting its own query string.
 */
function initialUrl(): string {
  try {
    const [nav] = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    if (nav?.name) return nav.name;
  } catch {
    // Fall through to the live URL.
  }
  return window.location.href;
}

/** Explicit override, so embed mode can be inspected at top level. */
function detectOverride(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URL(initialUrl()).searchParams.get(EMBED_PARAM) === EMBED_VALUE;
  } catch {
    return false;
  }
}

const framed: boolean = detectCrossOriginFrame() || detectOverride();

export type EmbedMode = "frame" | "app";

/** The token the Finpilot Notetaker app appends to its web view's agent. */
const APP_SHELL_USER_AGENT = /\bFinpilotNotetaker\//;

/**
 * Read when asked rather than latched: the user agent is constant for the
 * life of a document, so reading it late is harmless, and a story can put the
 * page in app mode by stubbing `navigator.userAgent` before it renders.
 */
function detectAppShell(): boolean {
  if (typeof navigator === "undefined") return false;
  return APP_SHELL_USER_AGENT.test(navigator.userAgent);
}

export function embedMode(): EmbedMode | null {
  if (framed) return "frame";
  return detectAppShell() ? "app" : null;
}

/** True in both modes: the application navigation is absent in each. */
export function isEmbedded(): boolean {
  return embedMode() !== null;
}
